import type { FC } from "react";
import { Form } from "react-bootstrap";
import { z } from "zod/v4";
import { toFormikValidationSchema } from "zod-formik-adapter";
import { useForm } from "../common/form/useForm";
import { Modal, useModal } from "../common/modal/Modal";
import { ExtraForm } from "../common/pure/ExtraFormGroup";
import { useCompanyActions } from "../company/useCompany";
import { Button } from "../components/Button";

const Schema = z.object({
	name: z
		.string()
		.min(3, "Name must be at least 3 characters long.")
		.max(255, "Name must be no longer than 255 characters."),
});

type Props = {
	companyId: number;
	currentName: string;
};

export const ModalRenameCompany: FC<Readonly<Props>> = ({
	companyId,
	currentName,
}) => {
	const { renameCompany } = useCompanyActions();
	const { mutateAsync, isPending } = renameCompany;
	const { close } = useModal();

	const form = useForm({
		initialValues: {
			name: currentName,
		},
		validationSchema: toFormikValidationSchema(Schema),
		onSubmit: async (values) => {
			await mutateAsync({ id: companyId, name: values.name.trim() });
			close();
		},
	});

	return (
		<Modal>
			<h2>Rename company</h2>

			<Form onSubmit={form.handleSubmit}>
				<ExtraForm.Group label="Name" required>
					<ExtraForm.Control
						placeholder="Company Name"
						autoFocus
						data-testid="rename-company-name"
						{...form.getFieldProps("name")}
						{...form.getFieldState("name")}
					/>
				</ExtraForm.Group>

				<Button full variant="primary" loading={isPending} type="submit">
					Save
				</Button>
			</Form>
		</Modal>
	);
};
